import React from "react";
import { useIntl } from "react-intl";
import Selector from "../../../../components/selector";
import { OccupancyOptionsEntity } from "../../../occupancyTypes/domain/OccupancyOptions.entity";
import { OccupancyGrid, OccupancyItem, OccupancyItemLabel } from "../styles";
import { messages } from "../bookingForm.messages";
import { NumericOption, OccupancyState, rangeOptions } from "../hooks/useOccupancyForm";
import { PropertyMaxOccupancy } from "../hooks/useBookingFormConfiguration";
import { OccupancyStepper } from "./OccupancyStepper";

interface AgeBasedOccupancyProps {
  occupancy: OccupancyState;
  childrenAges: (number | null)[];
  maxOccupancy: PropertyMaxOccupancy;
  occupancyOptions: OccupancyOptionsEntity;
  onStepOccupancy: (key: keyof OccupancyState, delta: number, max: number) => void;
  onStepChildrenCount: (delta: number, max: number) => void;
  onChangeChildAge: (index: number, option: NumericOption | null) => void;
}

function childAgeRange(options: OccupancyOptionsEntity): { min: number; max: number } {
  const min = options.minBabiesAge ?? options.minChildrenAge ?? 0;
  const max = options.maxTeensAge ?? options.maxChildrenAge ?? 17;
  return { min, max: Math.max(min, max) };
}

export function AgeBasedOccupancy({
  occupancy,
  childrenAges,
  maxOccupancy,
  occupancyOptions,
  onStepOccupancy,
  onStepChildrenCount,
  onChangeChildAge,
}: AgeBasedOccupancyProps) {
  const intl = useIntl();

  const maxChildrenByAge =
    (maxOccupancy.maxChildren ?? 0) +
    (maxOccupancy.maxJuniors ?? 0) +
    (maxOccupancy.maxBabies ?? 0);
  const ageRange = childAgeRange(occupancyOptions);

  return (
    <>
      <OccupancyGrid>
        {(maxOccupancy.maxAdults ?? 0) > 0 && (
          <OccupancyStepper
            label={intl.formatMessage(messages.adults)}
            value={occupancy.adults}
            max={maxOccupancy.maxAdults ?? 0}
            onStep={delta => onStepOccupancy("adults", delta, maxOccupancy.maxAdults ?? 0)}
          />
        )}

        {maxChildrenByAge > 0 && (
          <OccupancyStepper
            label={intl.formatMessage(messages.childrenCount)}
            value={childrenAges.length}
            max={maxChildrenByAge}
            onStep={delta => onStepChildrenCount(delta, maxChildrenByAge)}
          />
        )}
      </OccupancyGrid>

      {childrenAges.length > 0 && (
        <OccupancyGrid>
          {childrenAges.map((age, index) => {
            const options = rangeOptions(ageRange.min, ageRange.max);
            return (
              <OccupancyItem key={index}>
                <OccupancyItemLabel>
                  {intl.formatMessage(messages.childAge, { index: index + 1 })}
                </OccupancyItemLabel>
                <Selector
                  options={options}
                  value={options.find(option => option.value === age) ?? null}
                  onChange={(option: NumericOption | null) => onChangeChildAge(index, option)}
                  placeholder="-"
                />
              </OccupancyItem>
            );
          })}
        </OccupancyGrid>
      )}
    </>
  );
}
